import type { NormalizedHeldInput } from "./events";

export const FIXED_PHASES = Object.freeze([
  "apply-lifecycle-and-actor-removals",
  "evaluate-zero-labor-freeze",
  "publish-nav-purchase",
  "sample-player-intent",
  "build-task-snapshot",
  "arbitrate-work-and-leases",
  "advance-navigation",
  "arbitrate-triggers",
  "commit-transfers",
  "complete-node-reactions",
  "finalize-tender-events-and-checkpoints",
  "hash-gameplay-state",
] as const);

export type FixedPhase = (typeof FIXED_PHASES)[number];

export interface KernelTickInput {
  readonly tick: number;
  readonly tickMs: 50;
  readonly heldInput: Readonly<NormalizedHeldInput>;
}

export interface PhaseContext extends KernelTickInput {
  readonly phase: FixedPhase;
  readonly laborEnabledCount: number;
}

export type PhaseHandler<State> = (
  state: Readonly<State>,
  context: Readonly<PhaseContext>,
) => State | Readonly<State> | void;

export interface KernelSnapshot<State> {
  readonly state: Readonly<State>;
  readonly gameplayHash: string;
  readonly executedPhases: readonly FixedPhase[];
}

export interface SimulationKernelOptions<State> {
  readonly initialState: State;
  readonly hashGameplayState: (state: Readonly<State>) => string;
  readonly getLaborEnabledCount: (state: Readonly<State>) => number;
  readonly phaseHandlers?: Readonly<Partial<Record<FixedPhase, PhaseHandler<State>>>>;
}

function clonePlainData(value: unknown, ancestors: ReadonlySet<object>): unknown {
  if (value === null || typeof value === "string" || typeof value === "boolean") {
    return value;
  }
  if (typeof value === "number") {
    if (!Number.isFinite(value)) {
      throw new TypeError("plain-data numbers must be finite");
    }
    return value;
  }
  if (typeof value !== "object") {
    throw new TypeError("snapshots accept only plain data");
  }
  if (ancestors.has(value)) {
    throw new TypeError("plain data cannot contain cycles");
  }

  const nextAncestors = new Set(ancestors).add(value);
  if (Array.isArray(value)) {
    return Object.freeze(value.map((entry) => clonePlainData(entry, nextAncestors)));
  }

  const prototype = Object.getPrototypeOf(value) as object | null;
  if (prototype !== Object.prototype && prototype !== null) {
    throw new TypeError("snapshots accept only arrays and plain records");
  }

  const clone: Record<string, unknown> = {};
  for (const key of Reflect.ownKeys(value)) {
    if (typeof key !== "string") {
      throw new TypeError("plain-data records cannot have symbol keys");
    }
    const descriptor = Object.getOwnPropertyDescriptor(value, key);
    if (descriptor === undefined || !descriptor.enumerable || !("value" in descriptor)) {
      throw new TypeError("plain-data properties must be enumerable data values");
    }
    Object.defineProperty(clone, key, {
      value: clonePlainData(descriptor.value, nextAncestors),
      enumerable: true,
      configurable: true,
      writable: true,
    });
  }
  return Object.freeze(clone);
}

export function cloneAndFreezePlainData<Value>(value: Value): Readonly<Value> {
  return clonePlainData(value, new Set()) as Readonly<Value>;
}

function assertTickInput(input: KernelTickInput): void {
  if (!Number.isSafeInteger(input.tick) || input.tick < 1) {
    throw new RangeError("tick must be a positive safe integer");
  }
  if (input.tickMs !== 50) {
    throw new RangeError("kernel tick duration must be exactly 50 ms");
  }
}

function assertLaborEnabledCount(value: number): void {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new RangeError("laborEnabledCount must be a non-negative safe integer");
  }
}

function assertGameplayHash(value: string): void {
  if (value.length === 0) {
    throw new TypeError("gameplay hash must be a non-empty string");
  }
}

export class SimulationKernel<State> {
  readonly #hashGameplayState: (state: Readonly<State>) => string;
  readonly #getLaborEnabledCount: (state: Readonly<State>) => number;
  readonly #phaseHandlers: Readonly<Partial<Record<FixedPhase, PhaseHandler<State>>>>;
  readonly #initialSnapshot: Readonly<KernelSnapshot<State>>;

  constructor(options: SimulationKernelOptions<State>) {
    this.#hashGameplayState = options.hashGameplayState;
    this.#getLaborEnabledCount = options.getLaborEnabledCount;
    this.#phaseHandlers = Object.freeze({ ...options.phaseHandlers });

    const state = cloneAndFreezePlainData(options.initialState);
    const gameplayHash = this.#hashGameplayState(state);
    assertGameplayHash(gameplayHash);
    this.#initialSnapshot = Object.freeze({
      state,
      gameplayHash,
      executedPhases: Object.freeze([]),
    });
  }

  get initialSnapshot(): Readonly<KernelSnapshot<State>> {
    return this.#initialSnapshot;
  }

  tick(
    snapshot: Readonly<KernelSnapshot<State>>,
    input: Readonly<KernelTickInput>,
  ): Readonly<KernelSnapshot<State>> {
    assertTickInput(input);
    const laborEnabledCountAtTickStart = this.#getLaborEnabledCount(snapshot.state);
    assertLaborEnabledCount(laborEnabledCountAtTickStart);

    let state = snapshot.state;
    let laborEnabledCount = laborEnabledCountAtTickStart;
    const executedPhases: FixedPhase[] = [];
    for (const phase of FIXED_PHASES) {
      if (phase === "evaluate-zero-labor-freeze") {
        laborEnabledCount = this.#getLaborEnabledCount(state);
        assertLaborEnabledCount(laborEnabledCount);
      }
      const zeroLaborBlocked =
        laborEnabledCount === 0 &&
        phase !== "apply-lifecycle-and-actor-removals" &&
        phase !== "evaluate-zero-labor-freeze" &&
        phase !== "hash-gameplay-state";
      if (zeroLaborBlocked) {
        continue;
      }

      executedPhases.push(phase);
      const handler = this.#phaseHandlers[phase];
      const nextState = handler?.(
        state,
        Object.freeze({ ...input, phase, laborEnabledCount }),
      );
      const mutationBlocked =
        laborEnabledCountAtTickStart === 0 ||
        (laborEnabledCount === 0 && phase !== "apply-lifecycle-and-actor-removals");
      if (!mutationBlocked && nextState !== undefined && nextState !== state) {
        state = cloneAndFreezePlainData(nextState);
      }
    }

    const gameplayHash = this.#hashGameplayState(state);
    assertGameplayHash(gameplayHash);
    if (laborEnabledCountAtTickStart === 0 && gameplayHash !== snapshot.gameplayHash) {
      throw new Error("zero-labor gameplay hash changed");
    }
    return Object.freeze({
      state,
      gameplayHash,
      executedPhases: Object.freeze(executedPhases),
    });
  }
}
